import { DEFAULT_DESIGN_SYSTEM_STATE } from '../lib/design-system-options'
import { KO_DESIGN_AXES_COPY, type DesignAxesCopy } from './axes-copy'
import type { Advice, AxisName, AxisResolution } from './types'

/**
 * gap 인 축에 제안을 붙인다. 값은 바꾸지 않는다 — 사용자가 정할 때까지 gap 이다.
 * auto mode 는 이 제안을 그대로 적용한다(Task 5). 별도의 결정 엔진을 두지 않는
 * 이유는, 두면 화면이 권하는 값과 auto 가 고르는 값이 갈라지기 때문이다.
 * 문구는 `DesignAxesCopy` — React 표면은 로케일 사전을, 순수 호출자는 KO
 * 기본값을 쓴다 (axes-copy.ts 참조).
 */
export function adviseAxes(
  axes: Record<AxisName, AxisResolution>,
  copy: DesignAxesCopy = KO_DESIGN_AXES_COPY,
): Record<AxisName, AxisResolution> {
  const result = {} as Record<AxisName, AxisResolution>

  for (const [key, resolution] of Object.entries(axes) as [AxisName, AxisResolution][]) {
    if (resolution.origin !== 'gap') {
      result[key] = { ...resolution, advice: [...resolution.advice] }
      continue
    }
    const suggested = String(DEFAULT_DESIGN_SYSTEM_STATE[key])
    const kept = resolution.advice.filter((item) => item.declined)
    // "잔소리 안 하기"는 declined 레코드를 남기는 것만으로는 안 지켜진다 —
    // 같은 제안이 undeclined 상태로 나란히 또 붙으면 사용자 입장에선 여전히
    // "거절했는데 또 뜬다"이다. 그래서 kept 안에 지금 낼 제안과 값이 같은
    // declined 항목이 있으면 새로 붙이지 않는다. 비교 기준은 severity/message
    // 전체가 아니라 suggestion.value 하나뿐이다 — value 는 실제로 적용될
    // 내용이고, message/이유 문구가 나중에 카피만 바뀌어도 "다른 제안"으로
    // 오인해 다시 잔소리하는 것을 막는다. 반대로 value 가 달라졌다면(소스나
    // 기본값이 바뀌어 추천이 바뀐 경우) 사용자가 거절한 적 없는 새 정보이므로
    // 다시 보여준다 — "declined 가 하나라도 있으면 무조건 억제"가 아니라
    // "같은 값이 declined 되어 있을 때만 억제".
    const alreadyDeclinedThisSuggestion = kept.some(
      (item) => item.suggestion?.value === suggested,
    )
    if (alreadyDeclinedThisSuggestion) {
      result[key] = { ...resolution, advice: kept }
      continue
    }
    // `suggested` is BOTH the value this advice offers and the value already
    // in force: `DEFAULT_DESIGN_SYSTEM_STATE` is what the store holds for an
    // axis nobody has chosen, and what auto-save persists as the project's
    // tokens. So the message names it rather than warning that nothing will
    // reach the app — see `DesignAxesCopy.adviceNotDecided` for the read-side
    // trail that established the old sentence was false.
    const fresh: Advice = {
      severity: 'note',
      message: copy.adviceNotDecided(suggested),
      suggestion: { value: suggested, why: copy.adviceWhy[key] },
    }
    result[key] = { ...resolution, advice: [...kept, fresh] }
  }

  return result
}
